<template>
  <form @submit.prevent="onSubmit">
    <div v-if="active" class="w-full flex flex-col mb-12">
      <BaseLoader v-if="submitting" class="w-10 h-10" />
      <template v-else-if="!submitted">
        <p class="mb-4">
          To learn more about this artwork, please provide your contact
          information.
        </p>
        <input
          ref="firstInput"
          class="work-input"
          autocomplete="name"
          placeholder="NAME"
          v-model="name"
        />
        <input
          class="work-input"
          autocomplete="email"
          placeholder="EMAIL ADDRESS"
          v-model="email"
        />
        <input
          class="work-input"
          autocomplete="tel"
          placeholder="PHONE"
          v-model="phone"
        />
        <div v-if="typeof valid === 'string'" class="mt-2 text-red-500 text-sm">
          {{ valid }}
        </div>
      </template>
      <div v-else-if="submitted">
        Thank you. Your inquiry has been received.
      </div>
    </div>

    <button
      v-if="!active"
      class="relative group block lg:inline uppercase tracking-widest mx-auto lg:mx-0"
    >
      <span class="link-dot-absolute" /> Inquire
    </button>
    <span
      v-else-if="submitting"
      class="uppercase tracking-widest mx-auto lg:mx-0"
    >
      Submitting...
    </span>
    <button
      v-else-if="!submitted"
      class="relative group block lg:inline uppercase tracking-widest mx-auto lg:mx-0"
    >
      <span class="link-dot-absolute" /> Submit
    </button>
  </form>
</template>

<script lang="ts">
import axios from 'axios';
import BaseLoader from '@/components/BaseLoader.vue';
import { defineComponent, nextTick, PropType, ref } from 'vue';
import { useForm } from '@/store/form';
import { Work } from '@/types';

export default defineComponent({
  components: {
    BaseLoader,
  },
  props: {
    work: { type: Object as PropType<Work>, required: true },
  },
  setup(props) {
    const firstInput = ref<HTMLInputElement | null>(null);
    const { name, email, phone, valid, validate } = useForm();
    const active = ref(false);
    const submitting = ref(false);
    const submitted = ref(false);

    const postInquiry = async () => {
      try {
        const params = new URLSearchParams();
        params.append('serial', props.work.serial);
        params.append('title', props.work.title);
        params.append('email', email.value);
        params.append('name', name.value);
        params.append('phone', phone.value);
        await axios.post(process.env.VUE_APP_INQUIRE_ENDPOINT, params);
        submitted.value = true;
      } catch (e) {
        submitted.value = false;
        valid.value = 'Something went wrong, please try again later.';
      } finally {
        submitting.value = false;
      }
    };

    const onSubmit = async () => {
      if (!active.value) {
        active.value = true;
        await nextTick();
        if (firstInput.value) {
          firstInput.value.focus();
        }
      } else {
        valid.value = validate();
        if (valid.value === true) {
          submitting.value = true;
          postInquiry();
        }
      }
    };

    return {
      submitting,
      submitted,
      firstInput,
      name,
      email,
      phone,
      active,
      onSubmit,
      valid,
    };
  },
});
</script>

<style lang="postcss" scoped>
.work-input {
  @apply my-1 border-b border-black placeholder-gray-500 outline-none bg-transparent;
  @apply pt-1;
}
</style>
